import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";

import { debounce } from "lodash";
import { Box, CircularProgress, Grid, Tooltip } from "@material-ui/core";

import { useTranslation } from "react-i18next";

import { getSellerShipments, reprocessShipment } from "api/shipment";

import { InternalLink } from "components/Custom/Links";
import { ProductTypeFilter, StatusLabel } from "components/Custom";
import {
  Button,
  DatePickerGroup,
  Dropdown,
  InputContainer,
} from "components/Form";
import { FormattedTextField } from "components/FormElements";
import PageTitle from "components/PageTitle";
import DefaultTable from "components/Table";

import { useUserState } from "context/UserContext";

import {
  Class,
  FolderSpecial,
  Printer,
  QrCode2,
  Refresh2,
  SnippetFolder,
  Style,
  TextSnippet,
} from "icons";

import {
  initialAPIListValue,
  ShipmentServiceTypes,
  ShipmentStatus,
} from "utils/constants";
import {
  convertDataToTableView,
  convertDataToTableViewObt,
} from "utils/convert";
import { timezoneFormat } from "utils/date";
import { exportToExcel } from "utils/exportToExcel";
import getClearAdornment from "utils/form";
import parseJwt from "utils/jwt";
import { getWithExpiry } from "utils/storage";
import { toTitleCase } from "utils/string";

import {
  ActionsButton,
  DeleteShipmentDialog,
  PartialShipDialog,
  RequestCancellationDialog,
  RevertShipmentDialog,
  RevertToPickedDialog,
  ShipmentCancelModal,
  ShipTheShipmentDialog,
} from "./ShipmentActions";
import ShipmentImport, { getTrackingNumber } from "./ShipmentImport";
import QuinceLabelsButton from "./QuinceLabels";

import * as XLSX from "xlsx";
import { popupNotification, useLayoutDispatch } from "context/LayoutContext";

const initialCells = [
  {
    id: "shipment_number",
    render: (r, exported) =>
      exported ? (
        r.shipment_number
      ) : (
        <InternalLink
          to={`/app/shipments/${r.id}`}
          children={r.shipment_number}
        />
      ),
    style: {
      minWidth: "150px",
    },
    sortable: true,
  },
  {
    id: "customer",
    render: (r) => r.customer_id,
  },
  {
    id: "po_number",
    render: (r) => r.po_number,
    style: {
      minWidth: "150px",
    },
  },
  {
    id: "sku",
    render: (r, exported) =>
      exported ? (
        r?.sku
      ) : (
        <Tooltip
          title={
            r.detail_skus?.length > 1 ? (
              <div>
                {r.detail_skus.slice(1).map((item, index) => (
                  <div key={index}>{item}</div>
                ))}
              </div>
            ) : null
          }
          arrow={true}
          disableHoverListener={r.detail_skus?.length <= 1}
          disableFocusListener={r.detail_skus?.length <= 1}
          disableTouchListener={r.detail_skus?.length <= 1}
        >
          <span>
            {r.detail_skus?.[0] +
              (r.detail_skus?.length > 1
                ? ` (+${r.detail_skus.length - 1})`
                : "") || "-"}
          </span>
        </Tooltip>
      ),
    style: {
      minWidth: "150px",
    },
    sortable: true,
  },
  {
    id: "ship_via",
    render: (r) => toTitleCase(r.ship_via),
    style: {
      minWidth: "120px",
    },
  },
  {
    id: "status",
    render: (r, exported) =>
      exported ? (
        toTitleCase(r.status)
      ) : (
        <StatusLabel status={toTitleCase(r.status)} />
      ),
  },
  {
    id: "process_date",
    label: "process date",
    render: (r) => (
      <span title={`Created at: ${timezoneFormat(r.created_date)}`}>
        {timezoneFormat(r.process_date)}
      </span>
    ),
    style: {
      minWidth: "150px",
    },
    export: false,
    sortable: true,
  },
];

export default function Shipments() {
  const layoutDispatch = useLayoutDispatch();
  const [t] = useTranslation();
  const history = useHistory();
  const claims = parseJwt(getWithExpiry("access"));
  const { related_product } = claims;
  const { permissions } = useUserState();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [search, setSearch] = useState({
    is_small_carrier: true, // default
    status: "",
    service_type: "",
    shipment_number: "",
    ship_via: "",
    order__po_number: "",
    order__customer_id: "",
    product_type: related_product || 1,
    start_date: "",
    end_date: "",
    seller_id: 2,
    sku: "",
  });
  const [orderDirection, setOrderDirection] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [shipments, setShipments] = useState(initialAPIListValue);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [exportDisabled, setExportDisabled] = useState(false);
  const [cancelRequestedShipmentID, setCancelRequestedShipmentID] = useState(
    false,
  );
  const [cancelShipmentID, setCancelShipmentID] = useState(false);
  const [cancellationOption, setCancellationOption] = useState("");
  const [revertShipmentID, setRevertShipmentID] = useState("");
  const [revertToPickedID, setRevertToPickedID] = useState("");
  const [deleteShipmentNumber, setDeleteShipmentNumber] = useState("");
  const [shipShipmentNumber, setShipShipmentNumber] = useState("");
  const [cells, setCells] = useState(initialCells);
  const [headerCounts, setHeaderCounts] = useState({});
  const [shipmentsImportData, setShipmentsImportData] = useState(
    initialAPIListValue,
  );
  const [duplicates, setDuplicates] = useState([]);
  const [reprocessLoading, setReprocessLoading] = useState(false);
  const [partialShipID, setPartialShipID] = useState("");

  const handleFileChange = (e) => {
    const files = e.target.files;
    const f = files[0];
    const reader = new FileReader();

    reader.onload = function (e) {
      const data = e.target.result;
      const readedData = XLSX.read(data, {
        type: "binary",
        cellDates: true,
        dateNF: "yyyy-mm-dd",
      });
      const wsname = readedData.SheetNames[0];
      const ws = readedData.Sheets[wsname];

      let values = XLSX.utils
        .sheet_to_json(ws, { raw: false })
        .filter((v) => v["PO Number"] && v["Customer Name"] && v["SKU"]);

      if (!values.length) {
        popupNotification({
          dispatch: layoutDispatch,
          message:
            "Please add PO Number, Customer Name, SKU columns in your excel file",
          status: "error",
        });
        return;
      }

      if (values.length > 500) {
        popupNotification({
          dispatch: layoutDispatch,
          message: "Shipment count cannot be more than 500.",
          status: "error",
        });
        return;
      }

      // Remove duplicates based on PO Number + SKU. The tracking is part of the key: with labels
      // the customer bought, the same SKU twice on a PO is two boxes with two labels.
      const poSkuSet = new Set();
      const uniqueValues = [];
      const d = [];
      values.forEach((value) => {
        const po = value["PO Number"];
        const sku = value["SKU"];
        const key = `${po}__${sku}__${getTrackingNumber(value)}`;
        if (!poSkuSet.has(key)) {
          poSkuSet.add(key);
          uniqueValues.push(value);
        } else {
          d.push(value);
        }
      });
      values = uniqueValues;
      setDuplicates(d);

      // generate shipment and order #
      values.forEach((value, index) => {
        const first_char = value["Marketplace"][0].toLowerCase();
        const sn =
          "obt-" +
          first_char +
          value["Marketplace"]
            .slice(1)
            .toLowerCase()
            .replace(/[aeiou&%/?+#\s]/g, "")
            .substring(0, 2) +
          "-" +
          value["PO Number"];
        value["Shipment Number"] = sn;
        value["Order Number"] = sn;
        value["Line Number"] = index + 2;
      });

      setShipmentsImportData({
        count: values.length,
        items: values,
      });
    };
    reader.readAsBinaryString(f);
    e.target.value = "";
  };

  const obtExportCells = [
    {
      id: "customer",
      render: (r) => r.customer_id,
    },
    {
      id: "po_number",
      render: (r) => r.po_number,
      style: {
        minWidth: "150px",
      },
    },
    {
      id: "sku",
      render: (r) => r?.sku,
      style: {
        minWidth: "150px",
      },
      sortable: true,
    },
    {
      id: "ship_via",
      render: (r) => toTitleCase(r.ship_via),
      style: {
        minWidth: "120px",
      },
    },
    {
      id: "tracking_number",
      render: (r) => r.tracking_number,
      style: {
        minWidth: "150px",
      },
      sortable: true,
    },

    {
      id: "status",
      render: (r) => toTitleCase(r.status),
    },
    {
      id: "process_date",
      label: "process date",
      render: (r) => timezoneFormat(r.process_date),
    },
    {
      id: "created_date",
      label: "created_at",
      render: (r) => timezoneFormat(r.created_date),
    },
  ];

  const exportCells = [
    ...initialCells,
    {
      id: "process_date",
      label: "process date",
      render: (r) => timezoneFormat(r.process_date),
    },
    {
      id: "created_date",
      label: "created_at",
      render: (r) => timezoneFormat(r.created_date),
    },
  ];

  useEffect(() => {
    if (shipments.isFetching) {
      let params = {
        ...search,
        limit: rowsPerPage,
        offset: rowsPerPage * page,
      };
      if (orderDirection && orderBy) {
        params.ordering = `${orderDirection === "asc" ? "" : "-"}${orderBy}`;
      }
      getSellerShipments({
        params,
        setFunc: (res) => {
          setShipments(res);
          setHeaderCounts(res.customCounts);
          setSelectedShipments([]);
        },
        paginated: true,
      });
    }
  }, [
    search,
    page,
    rowsPerPage,
    shipments.isFetching,
    orderDirection,
    orderBy,
  ]);

  useEffect(() => {
    const hasCancelPermission = permissions?.includes(
      "contenttypes.shipment.cancel",
    );
    const hasShipShipment = permissions?.includes("contenttypes.shipment.ship");
    if (hasCancelPermission || hasShipShipment) {
      setCells([
        ...initialCells,
        {
          id: "actions",
          export: false,
          render: (r) => (
            <ActionsButton
              status={r.status}
              onCancelRequestClick={(option) => {
                setCancelRequestedShipmentID(r.id);
                setCancellationOption(option);
              }}
              onCancelClick={() => setCancelShipmentID(r.id)}
              onRevertClick={() => {
                setRevertShipmentID(r.id);
              }}
              onRevertToPickedClick={() => setRevertToPickedID(r.id)}
              onShipClick={() => {
                setShipShipmentNumber(r.shipment_number);
              }}
              onReprocessClick={() => {
                reprocessShipment({
                  id: r.id,
                  responseSetter: (res) => {
                    if (!res?.success && res?.message) {
                      popupNotification({
                        dispatch: layoutDispatch,
                        message: res.message,
                        status: "error",
                      });
                    } else if (res?.success) {
                      popupNotification({
                        dispatch: layoutDispatch,
                        message: "Shipment reprocessed successfully.",
                        status: "success",
                      });

                      setShipments({ ...shipments, isFetching: true });
                    }
                  },
                });
              }}
              onPartialShipClick={r.detail_skus?.length > 1 ? () => setPartialShipID(r.id) : null}
              onDeleteClick={() => setDeleteShipmentNumber(r.shipment_number)}
            />
          ),
        },
      ]);
    } else {
      setCells(initialCells);
    }
  }, [permissions]);

  const onSort = (header) => {
    const isAsc = orderBy === header && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(header);
    setShipments({ ...shipments, isFetching: true });
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
    setShipments((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangeRowsPerPage = ({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    debouncedSearchHandler();
  };

  const handleChangeFilterSelect = (prop) => ({ target: { value } }) => {
    setSearch((prev) => ({ ...prev, [prop]: value }));
    debouncedSearchHandler();
  };

  const handleChangeFilterInput = (prop) => ({ target: { value } }) => {
    setSearch((prev) => ({ ...prev, [prop]: value }));
    debouncedSearchHandler();
  };

  const exportData = (data) => {
    const today = new Date();
    exportToExcel(
      [],
      `${
        search.seller_id === 2 ? "OBT-" : ""
      }Shipments-${today.toLocaleDateString()}`,
      true,
      search.seller_id === 2
        ? convertDataToTableViewObt(data, obtExportCells)
        : convertDataToTableView(data, exportCells),
      setExportDisabled,
    );
  };

  const handleExportData = () => {
    if (search.seller_id === 2 || search.status === "Out_of_Stock") {
      getSellerShipments({
        params: {
          ...search,
          limit: shipments.count,
        },
        setFunc: ({ items: { results } }) => exportData(results),
      });
      return;
    }

    if (shipments.items.length === shipments.count) {
      exportData(shipments.items);
    } else {
      getSellerShipments({
        params: {
          ...search,
          limit: shipments.count,
        },
        setFunc: ({ items: { results } }) => exportData(results),
      });
    }
  };

  const clearHandler = (input) => {
    setSearch({ ...search, [input]: "" });
    debouncedSearchHandler();
  };

  const clearAdornment = (input) =>
    getClearAdornment(input, clearHandler, search);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    debounce(() => {
      setPage(0);
      setShipments({ ...shipments, isFetching: true });
    }, 500),
    [],
  );

  return (
    <>
      <PageTitle
        title={t("shipments")}
        multipleButtons={true}
        button={
          <>
            <Button variant="contained" color="primary-light" component="label">
              Import Shipments
              <input
                id="files"
                hidden
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                type="file"
                onChange={handleFileChange}
              />
            </Button>
            <QuinceLabelsButton
              onDone={() =>
                setShipments((prev) => ({ ...prev, isFetching: true }))
              }
            />
          </>
        }
      />
      {shipmentsImportData?.count ? (
        <ShipmentImport
          open={true}
          shipments={shipmentsImportData}
          duplicates={duplicates}
          close={(status) => {
            setShipmentsImportData(initialAPIListValue);
            setDuplicates([]);
            if (status) {
              setShipments((prev) => ({ ...prev, isFetching: true }));
            }
          }}
        />
      ) : null}

      <ShipmentCancelModal
        shipment_id={cancelShipmentID}
        close={() => setCancelShipmentID(false)}
        afterCancelled={() => setShipments({ ...shipments, isFetching: true })}
      />
      <RequestCancellationDialog
        shipment_id={cancelRequestedShipmentID}
        close={() => setCancelRequestedShipmentID(false)}
        afterCancelled={() => setShipments({ ...shipments, isFetching: true })}
        option={cancellationOption}
      />
      <RevertShipmentDialog
        shipment_id={revertShipmentID}
        close={(status = false) => {
          if (status) {
            setShipments({ ...shipments, isFetching: true });
          }
          setRevertShipmentID("");
        }}
      />
      <RevertToPickedDialog
        shipment_id={revertToPickedID}
        close={(status = false) => {
          if (status) {
            setShipments({ ...shipments, isFetching: true });
          }
          setRevertToPickedID("");
        }}
      />
      <DeleteShipmentDialog
        shipment_number={deleteShipmentNumber}
        close={(status = false) => {
          if (status) {
            setShipments({ ...shipments, isFetching: true });
          }
          setDeleteShipmentNumber("");
        }}
      />
      <ShipTheShipmentDialog
        shipment_number={shipShipmentNumber}
        close={(status = false) => {
          if (status) {
            setShipments({ ...shipments, isFetching: true });
          }
          setShipShipmentNumber("");
          setSelectedShipments([]);
        }}
      />
      <PartialShipDialog
        shipment_id={partialShipID}
        close={(status = false) => {
          if (status) {
            setShipments({ ...shipments, isFetching: true });
          }
          setPartialShipID("");
        }}
      />
      <InputContainer>
        <Grid container>
          <DatePickerGroup
            startDateValue={search.start_date}
            startDateOnChange={handleChangeFilterSelect("start_date")}
            endDateValue={search.end_date}
            endDateOnChange={handleChangeFilterSelect("end_date")}
            itemGridProps={{ xs: 12, sm: 6, md: 4, lg: 2, xl: 2 }}
          />
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="shipment_number"
              label={t("shipment_number")}
              variant="outlined"
              fullWidth
              value={search.shipment_number}
              onChange={handleChangeFilterInput("shipment_number")}
              InputProps={clearAdornment("shipment_number")}
              LeftIcon={SnippetFolder}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="customer"
              label={t("customer")}
              variant="outlined"
              fullWidth
              value={search.order__customer_id}
              onChange={handleChangeFilterInput("order__customer_id")}
              InputProps={clearAdornment("order__customer_id")}
              LeftIcon={FolderSpecial}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="po_number"
              label="PO #"
              variant="outlined"
              fullWidth
              value={search.order__po_number}
              onChange={handleChangeFilterInput("order__po_number")}
              InputProps={clearAdornment("order__po_number")}
              LeftIcon={FolderSpecial}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="ship_via"
              label={t("ship_via")}
              variant="outlined"
              fullWidth
              value={search.ship_via}
              onChange={handleChangeFilterInput("ship_via")}
              InputProps={clearAdornment("ship_via")}
              LeftIcon={TextSnippet}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Dropdown
              id="service_type"
              title={t("service_type")}
              value={search.service_type}
              onChange={handleChangeFilterSelect("service_type")}
              items={ShipmentServiceTypes}
              LeftIcon={Style}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Dropdown
              id="status"
              title={t("status")}
              value={search.status}
              onChange={handleChangeFilterSelect("status")}
              items={ShipmentStatus.map((item) => ({
                value: item,
                label: item,
              }))}
              LeftIcon={Class}
            />
          </Grid>
          <ProductTypeFilter
            value={search.product_type}
            setValue={(product_type) => setSearch({ ...search, product_type })}
            onChange={handleChangeFilterSelect("product_type")}
          />
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="sku"
              label={t("SKU")}
              variant="outlined"
              fullWidth
              value={search.sku}
              onChange={handleChangeFilterInput("sku")}
              InputProps={clearAdornment("sku")}
              LeftIcon={QrCode2}
            />
          </Grid>
          <Box
            component={Grid}
            item
            lg={
              permissions?.includes("contenttypes.shipper.download_labels")
                ? 0
                : 3
            }
            display={{
              xs: "none",
              sm: "none",
              md: "none",
              lg: "block",
            }}
            style={{ borderRight: "none" }}
          />
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Button
              variant="contained"
              size="medium"
              color="secondary"
              onClick={() => {
                history.push("/app/shipments-to-print");
              }}
            >
              <Printer color="white" />{" "}
              <span style={{ marginLeft: 5 }}>Shipments To Print</span>
            </Button>
          </Grid>
          {search.status === "Picked" && (
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Button
                color="primary"
                disabled={!selectedShipments.length}
                onClick={() => {
                  setShipShipmentNumber(
                    selectedShipments.map(
                      (shipment) => shipment.shipment_number,
                    ),
                  );
                }}
              >
                Ship
              </Button>
            </Grid>
          )}
          {search.status === "Out_of_Stock" && (
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Button
                color="secondary"
                disabled={reprocessLoading || !shipments.items.length}
                onClick={() => {
                  setReprocessLoading(true);
                  reprocessShipment({
                    all: true,
                    responseSetter: (res) => {
                      setReprocessLoading(false);
                      if (!res?.success && res?.message) {
                        popupNotification({
                          dispatch: layoutDispatch,
                          message: res.message,
                          status: "error",
                        });
                      } else if (res?.success) {
                        popupNotification({
                          dispatch: layoutDispatch,
                          message:
                            "Shipments reprocessing started. It may take a while.",
                          status: "success",
                        });

                        setShipments({ ...shipments, isFetching: true });
                      }
                    },
                  });
                }}
              >
                {reprocessLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Refresh2 color="white" />
                    <span>Re-Process All</span>
                  </>
                )}
              </Button>
            </Grid>
          )}
        </Grid>
      </InputContainer>
      <Grid container spacing={4}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <DefaultTable
            data={shipments}
            headers={cells}
            headerCounts={headerCounts}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleExportData={handleExportData}
            sortActions={{ onSort, orderBy, orderDirection }}
            exportDisabled={exportDisabled}
            setExportDisabled={setExportDisabled}
            filters={search}
            hasCheckBox={search.status === "Picked"}
            selected={selectedShipments}
            setSelected={setSelectedShipments}
          />
        </Grid>
      </Grid>
    </>
  );
}
